import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useEffect } from 'react';

export interface GuardEvaluation {
  performance_rating?: number; // 1-5 stars (αξιολογιση αποδοσης)
  compliance_status?: number;  // 1-5 stars (κατασταση συμόρφωσης)
  notes?: string;
  evaluation_date?: string;
}

export interface SupervisorReport {
  id: string;
  supervisor_id: string;
  supervisor_name: string | null;
  site_id: string | null;
  guard_id: string | null;
  team_id: string | null;
  title: string;
  description: any;
  severity: string;
  status: string;
  location: string | null;
  latitude: number | null;
  longitude: number | null;
  notes: any[];
  image_url: string | null;
  incident_time: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  updated_at: string;
  guard_evaluations?: GuardEvaluation;
}

export interface SupervisorReportHistory {
  id: string;
  report_id: string;
  user_id: string;
  user_name: string;
  action_type: string;
  previous_status: string | null;
  new_status: string | null;
  note: string | null;
  created_at: string;
}

export const useSupervisorReports = (teamId: string) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['supervisor-reports', teamId],
    queryFn: async (): Promise<SupervisorReport[]> => {
      // Check if user is a client first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if user is a client for this team
      const { data: clientTeam } = await supabase
        .from('client_teams')
        .select('team_id')
        .eq('client_id', user.id)
        .eq('team_id', teamId)
        .maybeSingle();

      let data, error;

      if (clientTeam) {
        // If user is a client, get sites assigned to their team first
        const { data: sites } = await supabase
          .from('guardian_sites')
          .select('id')
          .eq('team_id', teamId);

        const siteIds = sites?.map(site => site.id) || [];

        // Then filter supervisor reports by those site IDs
        const result = await supabase
          .from('supervisor_reports' as any)
          .select('*')
          .in('site_id', siteIds.length > 0 ? siteIds : ['no-sites'])
          .order('created_at', { ascending: false });
        
        data = result.data;
        error = result.error;
      } else {
        // For non-clients (admins, managers, guards), use team_id filter
        const result = await supabase
          .from('supervisor_reports' as any)
          .select('*')
          .eq('team_id', teamId)
          .order('created_at', { ascending: false });
        
        data = result.data;
        error = result.error;
      }

      if (error) throw error;
      return (data || []) as unknown as SupervisorReport[];
    },
    enabled: !!teamId,
  });

  // Real-time subscription
  useEffect(() => {
    if (!teamId) return;

    const channel = supabase
      .channel('supervisor_reports_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'supervisor_reports',
          filter: `team_id=eq.${teamId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['supervisor-reports', teamId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [teamId, queryClient]);

  return query;
};

export const useSupervisorReportHistory = (reportId: string) => {
  return useQuery({
    queryKey: ['supervisor-report-history', reportId],
    queryFn: async (): Promise<SupervisorReportHistory[]> => {
      const { data, error } = await supabase
        .from('supervisor_report_history' as any)
        .select('*')
        .eq('report_id', reportId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as unknown as SupervisorReportHistory[];
    },
    enabled: !!reportId,
  });
};

export const useUpdateSupervisorReportStatus = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      reportId, 
      status, 
      userId, 
      userName 
    }: { 
      reportId: string; 
      status: string; 
      userId: string; 
      userName: string; 
    }) => {
      // First get the current report to track previous status
      const { data: currentReport, error: fetchError } = await supabase
        .from('supervisor_reports' as any)
        .select('status')
        .eq('id', reportId)
        .single();

      if (fetchError) throw fetchError;

      // Update the report status
      const { error: updateError } = await supabase
        .from('supervisor_reports' as any)
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', reportId);

      if (updateError) throw updateError;

      // Add history entry
      const { error: historyError } = await supabase
        .from('supervisor_report_history' as any)
        .insert({
          report_id: reportId,
          user_id: userId,
          user_name: userName,
          action_type: 'status_change',
          previous_status: (currentReport as any).status,
          new_status: status,
          note: `Status changed from ${(currentReport as any).status} to ${status}`
        });

      if (historyError) throw historyError;

      // Send email notification for status change
      try {
        const { data: reportData } = await supabase
          .from('supervisor_reports' as any)
          .select(`
            title,
            site_id,
            team_id
          `)
          .eq('id', reportId)
          .single();

        // Get site name separately
        const { data: siteData } = await supabase
          .from('guardian_sites' as any)
          .select('name')
          .eq('id', (reportData as any)?.site_id)
          .single();

        if (reportData) {
          console.log('Sending status notification for report:', reportId);
          
          const result = await supabase.functions.invoke('send-supervisor-update-notification', {
            body: {
              reportId,
              updateType: 'status_change',
              newStatus: status,
              previousStatus: (currentReport as any).status,
              updatedBy: userId,
              updatedByName: userName,
              reportTitle: (reportData as any).title || 'Supervisor Report',
              siteName: (siteData as any)?.name || 'Unknown Site',
              siteId: (reportData as any).site_id,
              teamId: (reportData as any).team_id
            }
          });
          
          console.log('Notification result:', result);
        }
      } catch (notificationError) {
        console.error('Failed to send notification:', notificationError);
        // Don't throw here - status update should succeed even if notification fails
      }

      return { success: true };
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Success",
        description: `Status updated to ${variables.status}!`
      });
      queryClient.invalidateQueries({ queryKey: ['supervisor-reports'] });
      queryClient.invalidateQueries({ queryKey: ['supervisor-report-history'] });
    },
    onError: (error) => {
      console.error('Status update error:', error);
      toast({
        title: "Error",
        description: "Failed to update status. Please try again.",
        variant: "destructive"
      });
    }
  });
};

export const useAddSupervisorReportNote = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      reportId, 
      note, 
      userId, 
      userName 
    }: { 
      reportId: string; 
      note: string; 
      userId: string; 
      userName: string; 
    }) => {
      // First get the current report
      const { data: currentReport, error: fetchError } = await supabase
        .from('supervisor_reports' as any)
        .select('notes')
        .eq('id', reportId)
        .single();

      if (fetchError) throw fetchError;

      // Add the new note to the notes array
      const currentNotes = (currentReport as any).notes || [];
      const newNote = {
        text: note,
        user_id: userId,
        user_name: userName,
        created_at: new Date().toISOString()
      };
      const updatedNotes = [...currentNotes, newNote];

      // Update the report with the new note
      const { error: updateError } = await supabase
        .from('supervisor_reports' as any)
        .update({ 
          notes: updatedNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', reportId);

      if (updateError) throw updateError;

      // Add history entry
      const { error: historyError } = await supabase
        .from('supervisor_report_history' as any)
        .insert({
          report_id: reportId,
          user_id: userId,
          user_name: userName,
          action_type: 'note_added',
          note: note
        });

      if (historyError) throw historyError;

      // Send email notification for note addition
      try {
        const { data: reportData } = await supabase
          .from('supervisor_reports' as any)
          .select(`
            title,
            site_id,
            team_id
          `)
          .eq('id', reportId)
          .single();

        // Get site name separately
        const { data: siteData } = await supabase
          .from('guardian_sites' as any)
          .select('name')
          .eq('id', (reportData as any)?.site_id)
          .single();

        if (reportData) {
          console.log('Sending note notification for report:', reportId);
          
          const result = await supabase.functions.invoke('send-supervisor-update-notification', {
            body: {
              reportId,
              updateType: 'note_added',
              note,
              updatedBy: userId,
              updatedByName: userName,
              reportTitle: (reportData as any).title || 'Supervisor Report',
              siteName: (siteData as any)?.name || 'Unknown Site',
              siteId: (reportData as any).site_id,
              teamId: (reportData as any).team_id
            }
          });
          
          console.log('Notification result:', result);
        }
      } catch (notificationError) {
        console.error('Failed to send notification:', notificationError);
        // Don't throw here - note addition should succeed even if notification fails
      }

      return { success: true };
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Note added successfully!"
      });
      queryClient.invalidateQueries({ queryKey: ['supervisor-reports'] });
      queryClient.invalidateQueries({ queryKey: ['supervisor-report-history'] });
    },
    onError: (error) => {
      console.error('Add note error:', error);
      toast({
        title: "Error",
        description: "Failed to add note. Please try again.",
        variant: "destructive"
      });
    }
  });
};

export const useDeleteSupervisorReport = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      reportId, 
      userId, 
      userName 
    }: { 
      reportId: string; 
      userId: string; 
      userName: string; 
    }) => {
      // Add history entry before deletion
      const { error: historyError } = await supabase
        .from('supervisor_report_history' as any)
        .insert({
          report_id: reportId,
          user_id: userId,
          user_name: userName,
          action_type: 'report_deleted',
          note: 'Report deleted by administrator'
        });

      if (historyError) throw historyError;

      // Delete the report
      const { error: deleteError } = await supabase
        .from('supervisor_reports' as any)
        .delete()
        .eq('id', reportId);

      if (deleteError) throw deleteError;

      return { success: true };
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Report deleted successfully!"
      });
      queryClient.invalidateQueries({ queryKey: ['supervisor-reports'] });
    },
    onError: (error) => {
      console.error('Delete report error:', error);
      toast({
        title: "Error",
        description: "Failed to delete report. Please try again.",
        variant: "destructive"
      });
    }
  });
};

// New interfaces for guard evaluation summaries
export interface GuardEvaluationSummary {
  guard_id: string;
  guard_name: string;
  avg_performance_rating: number;
  avg_compliance_status: number;
  overall_average: number;
  total_evaluations: number;
  performance_status: string;
  compliance_status_text: string;
  overall_status: string;
  last_evaluation_date?: string;
}

// Hook to get guard evaluation averages for a specific guard
export const useGuardEvaluationAverages = (guardId: string, startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ['guard-evaluation-averages', guardId, startDate, endDate],
    queryFn: async (): Promise<GuardEvaluationSummary> => {
      const { data, error } = await supabase.rpc('calculate_guard_evaluation_averages', {
        p_guard_id: guardId,
        p_start_date: startDate || null,
        p_end_date: endDate || null
      });

      if (error) throw error;
      return data?.[0] || {
        guard_id: guardId,
        guard_name: 'Unknown Guard',
        avg_performance_rating: 0,
        avg_compliance_status: 0,
        overall_average: 0,
        total_evaluations: 0,
        performance_status: 'No Data',
        compliance_status_text: 'No Data',
        overall_status: 'No Evaluations'
      };
    },
    enabled: !!guardId,
  });
};

// Hook to get team guard evaluations summary
export const useTeamGuardEvaluationsSummary = (teamId: string, startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ['team-guard-evaluations-summary', teamId, startDate, endDate],
    queryFn: async (): Promise<GuardEvaluationSummary[]> => {
      const { data, error } = await supabase.rpc('get_team_guard_evaluations_summary', {
        p_team_id: teamId,
        p_start_date: startDate || null,
        p_end_date: endDate || null
      });

      if (error) throw error;
      return data || [];
    },
    enabled: !!teamId,
  });
};

// Hook to update guard evaluations in a supervisor report
export const useUpdateGuardEvaluation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      reportId, 
      evaluation, 
      userId, 
      userName 
    }: { 
      reportId: string; 
      evaluation: GuardEvaluation; 
      userId: string; 
      userName: string; 
    }) => {
      // Update the report with the new evaluation
      const { error: updateError } = await supabase
        .from('supervisor_reports')
        .update({ 
          guard_evaluations: evaluation,
          updated_at: new Date().toISOString()
        })
        .eq('id', reportId);

      if (updateError) throw updateError;

      // Add history entry
      const { error: historyError } = await supabase
        .from('supervisor_report_history')
        .insert({
          report_id: reportId,
          user_id: userId,
          user_name: userName,
          action_type: 'evaluation_updated',
          note: `Guard evaluation updated - Performance: ${evaluation.performance_rating || 'N/A'}/5, Compliance: ${evaluation.compliance_status || 'N/A'}/5`
        });

      if (historyError) throw historyError;

      return { success: true };
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Guard evaluation updated successfully!"
      });
      queryClient.invalidateQueries({ queryKey: ['supervisor-reports'] });
      queryClient.invalidateQueries({ queryKey: ['guard-evaluation-averages'] });
      queryClient.invalidateQueries({ queryKey: ['team-guard-evaluations-summary'] });
      queryClient.invalidateQueries({ queryKey: ['supervisor-report-history'] });
    },
    onError: (error) => {
      console.error('Update evaluation error:', error);
      toast({
        title: "Error",
        description: "Failed to update guard evaluation. Please try again.",
        variant: "destructive"
      });
    }
  });
};